import * as Sentry from '@sentry/browser';
import _capitalize from 'lodash.capitalize';
import {
  BaseCreateFieldRequest,
  BaseUpdateFieldRequest,
  FieldResponse,
} from 'mapistry-shared';
import React from 'react';
import { ModalWizard } from '../../modalWizard/modalWizard';

export function getError(values: Partial<FieldResponse>) {
  // any error thrown in this function is unexpected - the UI should've protected against these
  const errMsg = `Tried to save field without all required field request properties`;
  Sentry.captureException(errMsg, { extra: { values } });
  return new Error(errMsg);
}

export function mapFormValuesToBaseRequestDto(
  values: Partial<FieldResponse>,
): BaseCreateFieldRequest | BaseUpdateFieldRequest {
  // these should always be defined by the time the form is getting submitted
  if (!values.type || !values.name) throw getError(values);

  const commonProperties = {
    name: values.name,
    description: values.description,
    isRequired: !!values.isRequired, // always send a defined isRequired
  };
  let baseRequestDto: BaseCreateFieldRequest | BaseUpdateFieldRequest;
  if (values.id) {
    baseRequestDto = { ...commonProperties, id: values.id };
  } else {
    baseRequestDto = { ...commonProperties, type: values.type };
  }

  return baseRequestDto;
}

interface BaseFieldWizardModalProps {
  field?: Partial<FieldResponse>;
  fieldTypeName?: string;
  onClose: () => void;
  onNextPage?: (values: Partial<FieldResponse>, pageNumber: number) => void;
  onPageSubmit: (values: Partial<FieldResponse>) => Promise<void>;
  showBackButton?: boolean;
  startPage?: number;
  wizardPages: React.ReactElement[];
}

export function BaseFieldWizardModal({
  field,
  fieldTypeName = 'field',
  onClose,
  onNextPage,
  onPageSubmit,
  showBackButton,
  startPage = 0,
  wizardPages,
}: BaseFieldWizardModalProps) {
  const fieldExists = !!field?.id;

  return (
    <ModalWizard<FieldResponse>
      initialValues={field}
      isEditing={fieldExists}
      itemLabel={fieldTypeName}
      onClose={onClose}
      onNextPage={onNextPage}
      onPageSubmit={onPageSubmit}
      showBackButton={showBackButton}
      startPage={startPage}
      title={`Configure ${_capitalize(fieldTypeName)}`}
      wizardPages={wizardPages}
    />
  );
}
